import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Dev/testing harness for the EXPERIMENTAL auto-resizing embed variant.
 *
 * Mimics a partner's website: some host-page content, then the test iframe
 * (the /embed/reseller-test/ route), then more content — so we can see the
 * widget sit inline and auto-resize to its content on a real device (mobile).
 *
 * Load: /embed-test?key=<grouping IframeKey>
 *
 * This never touches the live embed. It only exercises the reseller-test route
 * + the postMessage resize handshake, which is exactly the snippet the reseller
 * portal offers under "Testing embed (experimental)".
 */
export default function EmbedTestHarness() {
  const [params] = useSearchParams()
  const key = params.get('key') || ''
  const [lastHeight, setLastHeight] = useState(null)
  const origin = window.location.origin
  const src = key ? `${origin}/embed/reseller-test/${key}` : ''

  // The tiny listener the real snippet ships with — set the iframe height to
  // whatever the widget reports. Match by source window so it's robust with
  // multiple embeds on a page.
  useEffect(() => {
    function onMsg(e) {
      if (!e.data || e.data.type !== 'integolf-embed-resize') return
      const f = document.getElementById('integolf-embed-test')
      if (!f) return
      if (e.source && f.contentWindow && e.source !== f.contentWindow) return
      const h = e.data.height
      if (typeof h === 'number' && h > 0) {
        f.style.height = h + 'px'
        setLastHeight(h)
      }
    }
    window.addEventListener('message', onMsg)
    return () => window.removeEventListener('message', onMsg)
  }, [])

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16, fontFamily: 'system-ui, -apple-system, sans-serif', color: '#0f172a' }}>
      <div style={{ padding: '10px 12px', background: '#f1f5f9', borderRadius: 8, fontSize: 13, color: '#475569', marginBottom: 20 }}>
        <strong>Embed test harness</strong> — this page simulates a partner website.
        The widget below is the <code>/embed/reseller-test/</code> iframe and auto-resizes to fit.
        {lastHeight != null && <> Last reported height: <strong>{lastHeight}px</strong>.</>}
      </div>

      {!key && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b' }}>
          Add <code>?key=&lt;grouping IframeKey&gt;</code> to the URL. Find the key on the grouping's Embed tab.
        </div>
      )}

      {/* Host-page content ABOVE the widget */}
      <h1 style={{ fontSize: 26, marginBottom: 8 }}>Welcome to our golf resort</h1>
      <p style={{ color: '#475569', lineHeight: 1.7, maxWidth: 680 }}>
        Book one of our stay &amp; play packages below. This surrounding text is here so
        we can confirm the widget flows inline with the page and doesn&apos;t leave a
        fixed-height gap or an inner scrollbar on any screen size.
      </p>

      {key && (
        <iframe
          id="integolf-embed-test"
          title="Stay & Play widget (test)"
          src={src}
          width="100%"
          scrolling="no"
          frameBorder="0"
          allowTransparency="true"
          style={{ border: '1px dashed #cbd5e1', maxWidth: 1200, width: '100%', minHeight: 600, display: 'block', margin: '20px auto', background: 'transparent' }}
        />
      )}

      {/* Host-page content BELOW the widget — should sit right under the iframe,
          no gap, if auto-resize is working. */}
      <p style={{ color: '#475569', lineHeight: 1.7, maxWidth: 680 }}>
        Questions about your booking? Call us any time. This paragraph should sit
        immediately below the widget with no empty space when the iframe resizes correctly.
      </p>
    </div>
  )
}
